<template>
  <div class="wishlist-view">
    <!-- Success Message Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="3500"
      color="success"
      location="top right"
      rounded="lg"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
        {{ message }}
      </div>
    </v-snackbar>

    <!-- Header Section -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          to="/comics/homeview"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Comics Wishlist</h1>
          <p class="text-caption text-medium-emphasis mb-0">
            {{ displayPosts.length }} title{{ displayPosts.length === 1 ? '' : 's' }} planned to read
          </p>
        </div>
      </div>

      <div class="d-flex align-center ga-2">
        <v-btn
          to="/comics/add-post"
          color="#f59e0b"
          class="btn-modern text-white font-weight-bold"
          prepend-icon="mdi-plus"
        >
          Add to Wishlist
        </v-btn>
      </div>
    </div>

    <!-- Category Filter Pills Bar -->
    <div v-if="displayPosts.length" class="filter-pills-bar mb-6">
      <v-chip
        v-for="cat in categoryOptions"
        :key="cat"
        :variant="choice === cat ? 'flat' : 'tonal'"
        :color="choice === cat ? '#f59e0b' : undefined"
        class="filter-pill"
        size="small"
        @click="choice = cat"
      >
        {{ cat }}
      </v-chip>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="#f59e0b" size="48" width="4" />
      <div class="text-body-2 text-medium-emphasis mt-3">Loading wishlist...</div>
    </div>

    <v-alert v-else-if="error" type="error" class="mb-6" variant="tonal">{{ error }}</v-alert>

    <!-- Wishlist Poster Grid -->
    <div v-else-if="filteredPosts.length" class="poster-dense-grid">
      <div
        v-for="post in filteredPosts"
        :key="post.id"
        class="grid-poster-item"
      >
        <div class="poster-card poster-card--comics d-block">
          <div class="grid-poster-container">
            <router-link :to="{ name: 'ComicDetail', params: { id: post.id } }">
              <v-img
                :src="posterSrc(post)"
                :aspect-ratio="2 / 3"
                cover
                class="rounded-lg w-100"
              >
                <template #placeholder>
                  <div class="d-flex flex-column align-center justify-center fill-height fallback-poster">
                    <v-icon size="36" color="#f59e0b">mdi-draw</v-icon>
                    <span class="text-caption text-center px-2 mt-1 text-truncate w-100 font-weight-medium">
                      {{ post.title }}
                    </span>
                  </div>
                </template>
              </v-img>
            </router-link>

            <!-- Top Badges -->
            <div class="poster-badges-top">
              <v-chip size="x-small" color="blue" variant="flat" class="font-weight-bold">
                WISHLIST
              </v-chip>
            </div>

            <!-- Bottom Title & Quick Move Button -->
            <div class="poster-overlay-bottom">
              <div class="poster-title text-truncate font-weight-bold mb-2">{{ post.title }}</div>
              <v-btn
                size="x-small"
                color="#f59e0b"
                variant="flat"
                block
                class="btn-modern text-white font-weight-bold"
                prepend-icon="mdi-check-circle-outline"
                @click="moveToCollection(post.id)"
              >
                Move to Owned
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty Wishlist -->
    <v-card v-else class="glass-card pa-12 text-center my-6">
      <v-icon size="64" color="#f59e0b" class="mb-4">mdi-bookmark-outline</v-icon>
      <h3 class="text-h5 font-weight-bold mb-2">Your Comics Wishlist is Empty</h3>
      <p class="text-body-1 text-medium-emphasis mb-6 max-w-sm mx-auto">
        Keep track of comic series, issues, and manga you want to read next by saving them to your wishlist.
      </p>
      <v-btn
        to="/comics/add-post"
        color="#f59e0b"
        size="large"
        class="btn-modern text-white font-weight-bold"
        prepend-icon="mdi-plus"
      >
        Add Comic to Wishlist
      </v-btn>
    </v-card>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ComicsAPI from '@/services/comics'
import { CATEGORIES } from '@/config/comics'
import { posterSrc } from '@/utils/comicForm'

export default {
  name: 'ComicsWishList',
  setup() {
    const route = useRoute()
    const posts = ref([])
    const loading = ref(true)
    const error = ref('')
    const choice = ref('All')
    const message = ref(route.query.message || '')
    const showSnackbar = ref(!!route.query.message)

    const categoryOptions = ['All', ...CATEGORIES]

    const postMarked = (post) => {
      const value = post.marked
      if (value == null || value === '') return '0'
      return String(value)
    }

    const displayPosts = computed(() =>
      posts.value.filter((post) => postMarked(post) === '2'),
    )

    const filteredPosts = computed(() => {
      if (choice.value === 'All') return displayPosts.value
      return displayPosts.value.filter((p) => p.category === choice.value)
    })

    const loadPosts = async () => {
      loading.value = true
      error.value = ''
      try {
        const { data } = await ComicsAPI.getAllPost()
        posts.value = data.slice().reverse()
      } catch (err) {
        error.value = err.response?.data?.detail?.message || err.message || 'Failed to load wishlist'
      } finally {
        loading.value = false
      }
    }

    const moveToCollection = async (id) => {
      try {
        await ComicsAPI.moveToCollection(id)
        posts.value = posts.value.map((p) => (p.id === id ? { ...p, marked: '0' } : p))
        message.value = 'Comic moved to your collection!'
        showSnackbar.value = true
      } catch (err) {
        error.value = 'Failed to move comic to collection'
      }
    }

    watch(
      () => route.query.message,
      (value) => {
        if (value) {
          message.value = value
          showSnackbar.value = true
        }
      },
    )

    onMounted(loadPosts)

    return {
      posts,
      displayPosts,
      filteredPosts,
      loading,
      error,
      choice,
      message,
      showSnackbar,
      categoryOptions,
      moveToCollection,
      posterSrc,
    }
  },
}
</script>

<style scoped>
.wishlist-view {
  max-width: 1360px;
  margin: 0 auto;
}

.poster-dense-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}

@media (max-width: 600px) {
  .poster-dense-grid {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 12px;
  }
}

.grid-poster-container {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  background: rgba(128, 128, 128, 0.08);
}

.fallback-poster {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(251, 191, 36, 0.1));
}

.poster-badges-top {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
}

.poster-overlay-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 8px 8px 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.7) 70%, transparent 100%);
  color: #fff;
  z-index: 2;
}

.poster-title {
  font-size: 0.8rem;
  line-height: 1.2;
}

.max-w-sm {
  max-width: 420px;
}
</style>

